/* ==========================================================================
   Pack Simulator — UI + Pack Builder + Scoring + Store‑Wide Leaderboard
   + Global Pack Counter (fresh, no-cache fetches)
   ========================================================================== */
(function () {
  'use strict';

  const REV = '2025-08-15e';

  /* ----------------------------- Helpers ----------------------------- */
  const $  = (sel, el) => (el || document).querySelector(sel);
  const $$ = (sel, el) => Array.from((el || document).querySelectorAll(sel));
  const rand   = (n) => Math.floor(Math.random() * n);
  const choice = (arr) => arr.length ? arr[rand(arr.length)] : null;
  const chance = (num, den) => Math.random() < (num / den);
  const clamp  = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const round0 = (n) => Math.round(n);
  const fmt = (n) => (Number(n) || 0).toLocaleString();

  function waitForChecklist(maxMs = 6000) {
    return new Promise((resolve) => {
      const start = Date.now();
      (function tick () {
        if (window.CHECKLIST || window.__CHECKLIST_READY__) return resolve();
        if (Date.now() - start > maxMs) return resolve();
        requestAnimationFrame(tick);
      })();
    });
  }

  // fetch helper that forces fresh JSON without adding request headers
  async function getJSON(url, opts = {}) {
    const res = await fetch(url, {
      cache: 'no-store',
      mode: 'cors',
      credentials: 'omit',
      ...(opts || {})
    });
    return res.json();
  }

  /* ----------------------------- APIs ----------------------------- */
  const API_BASE = 'https://pack-sim-leaderboard.luke-349.workers.dev';
  const LEADERBOARD_API = API_BASE;
  const COUNTER_API = API_BASE; // same worker
  const LS_NAME_KEY = 'packSimLeaderboardName';

  /* ----------------------------- Global Counter (UI + network) ----------------------------- */
  function updateCounterUI(obj) {
    if (!obj) return;
    const t = $('#packsTotal'); if (t) t.textContent = fmt(obj.total);
    const w = $('#packsWeek');  if (w) w.textContent = fmt(obj.week);
  }

  async function refreshStatsIntoBar() {
    try {
      const d = await getJSON(`${COUNTER_API}/stats`);
      if (d && d.ok) updateCounterUI({ total: d.totalPacks, week: d.weekPacks });
    } catch (e) {}
  }

  async function bumpCounterThenRefresh(n = 1) {
    try {
      const d = await getJSON(`${COUNTER_API}/opened`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }, // preflight OK
        body: JSON.stringify({ n })
      });
      if (d && d.ok) updateCounterUI({ total: d.totalPacks, week: d.weekPacks });
    } catch (e) {}
  }

  /* ----------------------------- Leaderboard API ----------------------------- */
  function buildLeaderboardPayload(bestCard, name = '') {
    return {
      name: (name || '').trim(),
      card: {
        score: Number(bestCard.score || 0),
        player: String(bestCard.player || 'Unnamed'),
        team: String(bestCard.team || ''),
        typeLabel: String(bestCard.typeLabel || 'Base'),
        parallel: bestCard.parallel ? String(bestCard.parallel) : '',
        numberedLabel: bestCard.numberedLabel ? String(bestCard.numberedLabel) : ''
      }
    };
  }

  async function submitToLeaderboard(bestCard, tryName = null) {
    const body = buildLeaderboardPayload(
      bestCard,
      tryName ?? (localStorage.getItem(LS_NAME_KEY) || '')
    );
    const res = await fetch(`${LEADERBOARD_API}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify(body)
    });
    return res.json();
  }

  /** Use existing leaderboard container if present; create if missing. */
  function ensureLbRow() {
    let row = document.getElementById('lbRow');
    if (row) return row;

    let sec = document.getElementById('globalLeaderboard');
    if (!sec) {
      const after = document.getElementById('packResults');
      if (!after || !after.parentNode) return null;
      sec = document.createElement('section');
      sec.className = 'leaderboard';
      sec.id = 'globalLeaderboard';
      sec.innerHTML = `
        <div class="lb-head">
          <h3>Leaderboard — Weekly Top 10 Cards</h3>
          <p class="lb-desc">Shows the 10 highest-scoring single cards pulled this week. Resets weekly.</p>
        </div>
      `;
      after.parentNode.insertBefore(sec, after.nextSibling);
    }

    row = document.createElement('div');
    row.id = 'lbRow';
    row.className = 'lb-row';
    row.innerHTML = `<div class="lb-empty">Loading…</div>`;
    sec.appendChild(row);
    return row;
  }

  function toCardFromRemote(item) {
    const c = (item && item.card) || {};
    return {
      player: c.player,
      team: c.team,
      typeLabel: c.typeLabel,
      oddsText: oddsLabelFor(c.typeLabel),
      score: Number(c.score || 0),
      isAutograph: /auto|autograph/i.test(c.typeLabel),
      isNumbered: !!c.numberedLabel,
      parallel: c.parallel || '',
      numberedLabel: c.numberedLabel || ''
    };
  }

  function renderGlobalLeaderboard(weekKey, list) {
    const row = ensureLbRow();
    if (!row) return;

    row.innerHTML = '';
    if (!list || !list.length) {
      row.innerHTML = `<div class="lb-empty">No entries yet… be the first to make the board!</div>`;
      return;
    }

    for (const item of list) {
      const wrap = document.createElement('div');
      wrap.className = 'lb-item';
      wrap.appendChild(renderCard(toCardFromRemote(item)));

      const cap = document.createElement('div');
      cap.className = 'lb-caption';
      const name = (item.name || 'anonymous').trim() || 'anonymous';
      cap.textContent = `by ${name}`;
      wrap.appendChild(cap);

      row.appendChild(wrap);
    }
  }

  async function refreshGlobalLeaderboard() {
    try {
      const data = await getJSON(`${LEADERBOARD_API}/top`);
      if (data && data.ok) renderGlobalLeaderboard(data.week, data.top || []);
      else renderGlobalLeaderboard('', []);
    } catch (e) {
      renderGlobalLeaderboard('', []);
    }
  }

  /* ----------------------------- Club badges ----------------------------- */
  const CLUB_BADGES = {
    'Arsenal':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Arsenal_Badge.png?v=1755133610',
    'Nottingham Forest':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Nottingham_Forest_Badge.png?v=1755133610',
    'Bournemouth':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Bournemouth_Badge.png?v=1755133610',
    'Burnley':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Burnley_Badge.png?v=1755133610',
    'Brentford':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Brentford_Badge.png?v=1755133610',
    'Fulham':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Fulham_Badge.png?v=1755133609',
    'Aston Villa':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Aston_Villa_Badge.png?v=1755133610',
    'Wolves':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Wolves_Badge.png?v=1755133609',
    'Leeds United':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Leeds_United_Badge.png?v=1755133610',
    'Brighton':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Brighton_Badge.png?v=1755133610',
    'West Ham':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/West_Ham_Badge.png?v=1755133610',
    'Tottenham':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Tottenham_Badge.png?v=1755133610',
    'Newcastle United':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Newcastle_United_Badge.png?v=1755133610',
    'Crystal Palace':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Crystal_Palace_Badge.png?v=1755133611',
    'Manchester City':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Manchester_City_Badge.png?v=1755133610',
    'Manchester United':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Manchester_United_Badge.png?v=1755133610',
    'Liverpool':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Liverpool_Badge.png?v=1755133610',
    'Sunderland':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Sunderland_Badge.png?v=1755133611',
    'Everton':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Everton_Badge.png?v=1755133611',
    'Chelsea':'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/Chelsea_Badge.png?v=1755133610'
  };
  const TEAM_ALIASES = {
    'AFC Bournemouth':'Bournemouth',
    'Brighton & Hove Albion':'Brighton',
    'Wolverhampton Wanderers':'Wolves',
    'Burnley FC':'Burnley',
    'Liverpool FC':'Liverpool',
    'West Ham United':'West Ham',
    'Tottenham Hotspur':'Tottenham'
  };
  const teamKey = (t) => (TEAM_ALIASES[t] || t || '').trim();
  const badgeUrlFor = (team) => CLUB_BADGES[teamKey(team)] || null;

  const AUTO_STAMP =
    'https://cdn.shopify.com/s/files/1/0836/3294/7542/files/autograph.png?v=1755222084';

  /* ----------------------------- Odds & scores ----------------------------- */
  const ODDS = {
    basePerPack: [6, 7],
    commonInsertsPerPack: 2,
    uncommonInsertsPerPack: 1,
    chromeKingRate: 10,
    goldLionRate: 28,
    premierPullRate: 100,
    diamondRookieRate: 500,
    autographRate: 168,

    baseParallels: [
      { name: 'Green Parallel',  packRate: 8,  mult: 2.5 },
      { name: 'Yellow Parallel', packRate: 4,  mult: 2.0 },
      { name: 'Blue Parallel',   packRate: 2,  mult: 1.5 }
    ],

    numberedPackRate: 14,

    numberedTiersDefault: [
      { label: 'Mini Diamond — Agua #/499',   run: 499, mult: 3.0 },
      { label: 'Mini Diamond — Pink #/399',   run: 399, mult: 3.2 },
      { label: 'Mini Diamond — Yellow #/299', run: 299, mult: 3.2 },
      { label: 'Mini Diamond — Purple #/199', run: 199, mult: 3.5 },
      { label: 'Mini Diamond — Blue #/150',   run: 150, mult: 4.0 },
      { label: 'Mini Diamond — Green #/99',   run: 99,  mult: 4.5 },
      { label: 'Mini Diamond — Black & White #/75', run: 75, mult: 4.5 },
      { label: 'Mini Diamond — Gold #/50',    run: 50,  mult: 5.0 },
      { label: 'Mini Diamond — Orange #/25',  run: 25,  mult: 6.0 },
      { label: 'Mini Diamond — Black #/10',   run: 10,  mult: 8.0 },
      { label: 'Mini Diamond — Red #/5',      run: 5,   mult: 12.0 },
      { label: 'Mini Diamond — Platinum 1/1', run: 1,   mult: 20.0 }
    ],
    numberedTiersChrome: [
      { label: 'Gold Refractor #/50',   run: 50, mult: 5.0 },
      { label: 'Orange Refractor #/25', run: 25, mult: 6.0 },
      { label: 'Black Refractor #/10',  run: 10, mult: 8.0 },
      { label: 'Red Refractor #/5',     run: 5,  mult: 12.0 },
      { label: 'SuperFractor 1/1',      run: 1,  mult: 20.0 }
    ],

    baseScores: {
      'Base': 10, 'Common Insert': 20, 'Uncommon Insert': 30, 'Chrome King': 30,
      'Gold Lion': 35, 'Premier Pull': 40, 'Diamond Rookie': 100, 'Autograph': 80
    },

    oddsText: {
      Base: '6–7 per pack', 'Common Insert': '2 per pack', 'Uncommon Insert': '1 per pack',
      'Chrome King': '1 in 10 packs', 'Gold Lion': '1 in 28 packs', 'Premier Pull': '1 in 100 packs',
      'Diamond Rookie': '1 in 500 packs', 'Autograph': '1 in 168 packs'
    }
  };

  /* ----------------------------- Checklist buckets ----------------------------- */
  function bucketizeChecklist() {
    const CL = window.CHECKLIST || {};
    return {
      base: CL.base || [],
      generationNow: CL.generationNow || [],
      retroThreads: CL.retroThreads || [],
      proPartnerships: CL.proPartnerships || [],
      fullForce: CL.fullForce || [],
      tekkers: CL.tekkers || [],
      proPrecision: CL.proPrecision || [],
      beastMode: CL.beastMode || [],
      headliners: CL.headliners || [],
      chromeKings: CL.chromeKings || [],
      goldLions: CL.goldLions || [],
      premierPull: CL.premierPull || [],
      diamondRookies: CL.diamondRookies || [],
      baseAutos: CL.baseAutos || CL.baseAuto || [],
      beastModeAutos: CL.beastModeAutos || [],
      chromeKingsAutos: CL.chromeKingsAutos || [],
      blackEdgeAutos: CL.blackEdgeAutos || []
    };
  }

  /* Merge two-player names where present (Pro Partnerships, etc.) */
  function mergedPlayerName(item) {
    if (Array.isArray(item.players) && item.players.length) {
      return item.players.filter(Boolean).map(String).join(' & ');
    }
    const primary = String(
      item.player || item.name || item.player1 || item.playerA || ''
    ).trim();
    const candidateKeys = ['player2','playerB','player_b','partner','teammate','with','second'];
    let secondary = '';
    for (const k of candidateKeys) { if (item[k]) { secondary = String(item[k]).trim(); break; } }
    if (primary && secondary && primary.toLowerCase() !== secondary.toLowerCase()) {
      return `${primary} & ${secondary}`;
    }
    return primary || 'Unnamed Player';
  }

  function safePick(arr) {
    const item = choice(arr);
    if (!item) return null;
    return {
      player: mergedPlayerName(item),
      team: (item.team || item.team1 || item.club || item.club1 || ''),
      note: item.note || ''
    };
  }

  function cardBaseScore(typeLabel) {
    if (/^common insert/i.test(typeLabel)) return ODDS.baseScores['Common Insert'];
    if (/^uncommon insert/i.test(typeLabel)) return ODDS.baseScores['Uncommon Insert'];
    if (/chrome king/i.test(typeLabel))      return ODDS.baseScores['Chrome King'];
    if (/gold lion/i.test(typeLabel))        return ODDS.baseScores['Gold Lion'];
    if (/premier pull/i.test(typeLabel))     return ODDS.baseScores['Premier Pull'];
    if (/diamond rookie/i.test(typeLabel))   return ODDS.baseScores['Diamond Rookie'];
    if (/auto|autograph/i.test(typeLabel))   return ODDS.baseScores['Autograph'];
    return ODDS.baseScores['Base'];
  }

  function oddsLabelFor(typeLabel) {
    if (/^common insert/i.test(typeLabel)) return ODDS.oddsText['Common Insert'];
    if (/^uncommon insert/i.test(typeLabel)) return ODDS.oddsText['Uncommon Insert'];
    if (/chrome king/i.test(typeLabel)) return ODDS.oddsText['Chrome King'];
    if (/gold lion/i.test(typeLabel)) return ODDS.oddsText['Gold Lion'];
    if (/premier pull/i.test(typeLabel)) return ODDS.oddsText['Premier Pull'];
    if (/diamond rookie/i.test(typeLabel)) return ODDS.oddsText['Diamond Rookie'];
    if (/auto|autograph/i.test(typeLabel)) return ODDS.oddsText['Autograph'];
    return ODDS.oddsText['Base'];
  }

  /* ----------------------------- Numbered helpers ----------------------------- */
  function chooseNumberedTier(typeLabel) {
    const chrome = /chrome king/i.test(typeLabel);
    const tiers = chrome ? ODDS.numberedTiersChrome : ODDS.numberedTiersDefault;
    const total = tiers.reduce((s, t) => s + (t.run || 1), 0);
    let r = Math.random() * total;
    for (const t of tiers) { r -= (t.run || 1); if (r <= 0) return t; }
    return tiers[tiers.length - 1];
  }
  function eligibleForNumbered(card) {
    if (/auto|autograph/i.test(card.typeLabel)) return true;
    if (/chrome king/i.test(card.typeLabel))    return true;
    if (/^base$/i.test(card.typeLabel))         return true;
    if (/^common insert|^uncommon insert/i.test(card.typeLabel)) return true;
    return false;
  }

  /* ----------------------------- Parallels -> classes ----------------------------- */
  function addParallelClass(el, card) {
    let label = '';
    if (card.numberedLabel) label = card.numberedLabel.toLowerCase();
    else if (card.parallel) label = card.parallel.toLowerCase();
    if (!label) return;

    function add(slug, strong=false) {
      el.classList.add('par-shine', 'p-' + slug);
      if (strong) el.classList.add('par-strong');
    }
    if (/1\/1/.test(label))               { add('oneofone', true); return; }
    if (/#\/5\b|\b\/5\b/.test(label))     { add('red-strong', true); return; }
    if (/#\/10\b|\b\/10\b/.test(label))   { add('black-strong', true); return; }
    if (label.includes('orange')) add('orange');
    else if (label.includes('gold')) add('gold');
    else if (label.includes('purple')) add('purple');
    else if (label.includes('yellow')) add('yellow');
    else if (label.includes('green')) add('green');
    else if (label.includes('blue')) add('blue');
    else if (label.includes('aqua') || label.includes('agua')) add('agua');
    else if (label.includes('pink')) add('pink');
    else if (label.includes('black & white') || (label.includes('black') && label.includes('white'))) add('bw');
    else if (/\bblack\b/.test(label)) add('black');
    else if (label.includes('red')) add('red');
    else if (label.includes('platinum')) add('platinum');
  }

  /* ----------------------------- Card renderer ----------------------------- */
  function renderCard(card) {
    const el = document.createElement('div');
    el.className = 'card';
    el.dataset.score = String(card.score);
    if (card.isNumbered) el.classList.add('rarity-numbered');
    if (card.isAutograph) el.classList.add('rarity-auto', 'is-autograph');
    if (/diamond rookie/i.test(card.typeLabel)) el.classList.add('rarity-diamond');
    if (/gold lion/i.test(card.typeLabel)) el.classList.add('is-gold-lion');
    if (/^common insert/i.test(card.typeLabel)) el.classList.add('is-insert-common');
    if (/^uncommon insert/i.test(card.typeLabel)) el.classList.add('is-insert-uncommon');
    if (/chrome king/i.test(card.typeLabel)) el.classList.add('is-chrome-king');
    if (/premier pull/i.test(card.typeLabel)) el.classList.add('is-premier-pull');
    if (/pro\s*partnerships/i.test(card.typeLabel)) el.classList.add('is-pro-partnership');
    addParallelClass(el, card);

    const type = document.createElement('div');
    type.className = 'type'; type.textContent = card.typeLabel; el.appendChild(type);

    const art = document.createElement('div'); art.className = 'art';
    const big = document.createElement('div'); big.className = 'player-lg'; big.textContent = card.player; art.appendChild(big);

    const badgeUrl = badgeUrlFor(card.team);
    if (badgeUrl) {
      const img = document.createElement('img');
      img.className = 'club-badge-lg'; img.src = badgeUrl;
      img.alt = `${teamKey(card.team)} badge`; img.loading = 'lazy'; img.decoding = 'async';
      art.appendChild(img);
    }

    el.appendChild(art);

    if (card.isAutograph) {
      const meta = document.createElement('div'); meta.className = 'meta-auto';
      const sig  = document.createElement('img');
      sig.className = 'auto-stamp--outer';
      sig.src = AUTO_STAMP;
      sig.alt = 'Autograph'; sig.loading = 'lazy'; sig.decoding = 'async';
      meta.appendChild(sig); el.appendChild(meta);
    }

    if (card.parallel || card.numberedLabel) {
      const row = document.createElement('div'); row.className = 'row';
      if (card.parallel)      { const b = document.createElement('span'); b.className = 'badge badge-parallel'; b.textContent = card.parallel; row.appendChild(b); }
      if (card.numberedLabel) { const b = document.createElement('span'); b.className = 'badge badge-parallel'; b.textContent = card.numberedLabel; row.appendChild(b); }
      el.appendChild(row);
    }

    const odds = document.createElement('div'); odds.className = 'muted'; odds.textContent = `Odds: ${card.oddsText}`; el.appendChild(odds);
    const score = document.createElement('div'); score.className = 'tiny'; score.textContent = `Score: ${card.score}`; el.appendChild(score);
    return el;
  }

  /* ----------------------------- Build a pack ----------------------------- */
  function cardBase(typeLabel, it) {
    return {
      player: it.player, team: it.team, typeLabel,
      oddsText: oddsLabelFor(typeLabel),
      score: cardBaseScore(typeLabel),
      isAutograph: /auto|autograph/i.test(typeLabel),
      isNumbered: false, parallel: undefined, numberedLabel: undefined
    };
  }

  function buildPack(bk) {
    const cards = []; let slots = 10;

    function tryAddSpecial(rate, list, typeLabel) {
      if (slots === 0 || !list.length) return;
      if (chance(1, rate)) { const it = safePick(list); if (!it) return; cards.push(cardBase(typeLabel, it)); slots--; }
    }
    tryAddSpecial(ODDS.diamondRookieRate, bk.diamondRookies, 'Diamond Rookie');
    tryAddSpecial(ODDS.premierPullRate,   bk.premierPull,    'Premier Pull');
    tryAddSpecial(ODDS.goldLionRate,      bk.goldLions,      'Gold Lion');
    tryAddSpecial(ODDS.chromeKingRate,    bk.chromeKings,    'Chrome King');

    const allAutos = [].concat(bk.baseAutos, bk.beastModeAutos, bk.chromeKingsAutos, bk.blackEdgeAutos);
    tryAddSpecial(ODDS.autographRate, allAutos, 'Autograph');

    const uncommonSets = [
      ['proPrecision','Uncommon Insert — Pro Precision'],
      ['beastMode','Uncommon Insert — Beast Mode'],
      ['headliners','Uncommon Insert — Headliners']
    ].filter(([k]) => (bk[k] && bk[k].length));
    for (let i = 0; i < clamp(ODDS.uncommonInsertsPerPack, 0, slots); i++) {
      if (!uncommonSets.length) break;
      const [key, label] = choice(uncommonSets);
      const it = safePick(bk[key]); if (!it) break;
      cards.push(cardBase(label, it)); slots--;
    }

    const commonSets = [
      ['generationNow','Common Insert — Generation Now'],
      ['retroThreads','Common Insert — Retro Threads'],
      ['proPartnerships','Common Insert — Pro Partnerships'],
      ['fullForce','Common Insert — Full Force'],
      ['tekkers','Common Insert — Tekkers']
    ].filter(([k]) => (bk[k] && bk[k].length));
    for (let i = 0; i < clamp(ODDS.commonInsertsPerPack, 0, slots); i++) {
      if (!commonSets.length) break;
      const [key, label] = choice(commonSets);
      const it = safePick(bk[key]); if (!it) break;
      cards.push(cardBase(label, it)); slots--;
    }

    const baseTarget = choice(ODDS.basePerPack);
    const toFill = Math.max(baseTarget, slots);
    for (let i = 0; i < toFill && slots > 0; i++) {
      const it = safePick(bk.base); if (!it) break;
      cards.push(cardBase('Base', it)); slots--;
    }

    // at most one base color parallel per pack
    (function applyBaseColorParallelOnce() {
      const baseCards = cards.filter(c => /^base$/i.test(c.typeLabel));
      if (!baseCards.length) return;
      let chosen = null;
      for (const tier of ODDS.baseParallels) { if (chance(1, tier.packRate)) { chosen = tier; break; } }
      if (!chosen) return;
      const target = choice(baseCards);
      target.parallel = chosen.name;
      target.score = round0(target.score * chosen.mult);
    })();

    // optional numbered upgrade
    if (cards.length && chance(1, ODDS.numberedPackRate)) {
      const eligible = cards.filter(eligibleForNumbered);
      if (eligible.length) {
        const target = choice(eligible);
        const t = chooseNumberedTier(target.typeLabel);
        target.isNumbered = true; target.numberedLabel = t.label;
        if (target.parallel) delete target.parallel;
        target.score = round0(target.score * t.mult);
      }
    }

    return cards;
  }

  /* ----------------------------- Promo ----------------------------- */
  function maybeShowPromo() {
    const cfg = window.PACK_SIM_PROMO || {};
    if (!cfg.enabled) return;
    try {
      const key = 'packSimPromoShown';
      if (sessionStorage.getItem(key) === '1') return;
      const el = $('#packCount');
      const count = el ? (parseInt(el.textContent || '0', 10) || 0) : 0;
      const trigger = parseInt(cfg.triggerAfter || 10, 10);
      if (count >= trigger) {
        const modal = document.getElementById(cfg.modalId || 'promoModal'); if (!modal) return;
        wireDismissibleModal(modal);
        modal.classList.add('is-open'); modal.setAttribute('aria-hidden','false');
        document.addEventListener('keydown', modal.__keyHandler);
        sessionStorage.setItem(key, '1');
      }
    } catch(_) {}
  }

  /* ----------------------------- Robust modal wiring ----------------------------- */
  function wireDismissibleModal(modal) {
    if (!modal || modal.__wired) return;

    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', modal.__keyHandler);
    };

    modal.__keyHandler = (ev) => { if (ev.key === 'Escape') close(); };

    modal.addEventListener('click', (e) => {
      const isOverlay = e.target.classList && e.target.classList.contains('promo-overlay');
      const wantsClose = e.target.closest && e.target.closest('[data-close]');
      if (isOverlay || wantsClose) close();
    });

    modal.__close = close;
    modal.__wired = true;
  }

  /* ----------------------------- Render a pack ----------------------------- */
  function renderPack(cards, opts = { replay: false }) {
    const grid = $('#packResults'); if (!grid) return 0;
    cards.sort((a, b) => (b.score - a.score));
    grid.innerHTML = '';
    let total = 0;
    for (const c of cards) { total += c.score; grid.appendChild(renderCard(c)); }
    const packScoreEl = $('#packScore'); if (packScoreEl) packScoreEl.textContent = String(total);

    if (!opts.replay) {
      const packCountEl = $('#packCount');
      if (packCountEl) {
        const current = parseInt((packCountEl.textContent || '0'), 10) || 0;
        packCountEl.textContent = String(current + 1);
      }

      // increment global counter and refresh totals
      bumpCounterThenRefresh(1);

      // promo (with robust close wiring)
      maybeShowPromo();

      // leaderboard submission
      const bestCard = cards.reduce((a, b) => (b.score > a.score ? b : a), cards[0]);
      submitToLeaderboard(bestCard)
        .then(async (resp) => {
          if (resp && resp.ok && resp.inTop) {
            const saved = localStorage.getItem(LS_NAME_KEY) || '';
            if (!saved) {
              const nm = prompt(
                "🔥 Congrats! One of your cards made this week's Leaderboard.\n\nEnter your name (or leave blank to appear as “anonymous”):",
                ""
              );
              if (nm !== null) {
                try {
                  await submitToLeaderboard(bestCard, nm);
                  localStorage.setItem(LS_NAME_KEY, (nm || '').trim());
                } catch (_) {}
              }
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          refreshGlobalLeaderboard();
        });
    }
    return total;
  }

  /* ----------------------------- Previous / Best buttons ----------------------------- */
  function addHistoryButtons() {
    const controls = $('.controls'); if (!controls) return {};
    const prevBtn = document.createElement('button'); prevBtn.id='prevPackBtn'; prevBtn.className='btn-ghost'; prevBtn.textContent='Previous pack'; prevBtn.disabled=true;
    const bestBtn = document.createElement('button'); bestBtn.id='bestPackBtn'; bestBtn.className='btn-ghost'; bestBtn.textContent='Best pack'; bestBtn.disabled=true;
    const openBtn = $('#openPackBtn');
    if (openBtn && openBtn.nextSibling) { controls.insertBefore(prevBtn, openBtn.nextSibling); controls.insertBefore(bestBtn, prevBtn.nextSibling); }
    else { controls.appendChild(prevBtn); controls.appendChild(bestBtn); }
    return { prevBtn, bestBtn };
  }

  /* ----------------------------- Init ----------------------------- */
  function init() {
    const btn = $('#openPackBtn'); if (!btn) return;

    wireDismissibleModal(document.getElementById('promoModal'));
    wireDismissibleModal(document.getElementById('nameModal'));

    const { prevBtn, bestBtn } = addHistoryButtons();
    const buckets = bucketizeChecklist();

    ensureLbRow();
    refreshGlobalLeaderboard();
    refreshStatsIntoBar();            // initial totals

    // Light polling so you see other peoples’ activity without reloading
    setInterval(() => {
      refreshStatsIntoBar();
      refreshGlobalLeaderboard();
    }, 60000);

    const state = { current: null, previous: null, best: null };
    function updateHistoryButtons() {
      const el = $('#packCount');
      const count = el ? (parseInt(el.textContent || '0', 10) || 0) : 0;
      const enable = count >= 2;
      prevBtn.disabled = !enable || !state.previous;
      bestBtn.disabled = !enable || !state.best;
    }

    btn.addEventListener('click', () => {
      if (state.current) state.previous = state.current;
      const cards = buildPack(buckets);
      const total = cards.reduce((s, c) => s + c.score, 0);
      state.current = { cards: cards.map(c => ({ ...c })), score: total };
      if (!state.best || total > state.best.score) { state.best = { cards: cards.map(c => ({ ...c })), score: total }; }
      renderPack(cards, { replay: false });
      updateHistoryButtons();
    });

    if (prevBtn) prevBtn.addEventListener('click', () => { if (!state.previous) return; renderPack(state.previous.cards.map(c => ({ ...c })), { replay: true }); });
    if (bestBtn) bestBtn.addEventListener('click', () => { if (!state.best) return; renderPack(state.best.cards.map(c => ({ ...c })), { replay: true }); });
  }

  document.addEventListener('DOMContentLoaded', async () => { await waitForChecklist(); init(); });
})();
